import Axios from "../utils/Axios";
import { isAxiosError } from "axios";
import type { NestResult } from "../components/costing/nestingLayout";

export interface VerifiedDrawing {
  filename: string;
  status: "verified" | "error";
  error?: string;
  verification?: string;
  length?: number;
  width?: number;
  cut_length?: number;
  pierces?: number;
  warnings?: string[];
  preview?: number[][][];
}

export interface LaserInputs {
  thickness: number | "";
  quantity: number | "";
  cut_length: number | "";
  pierces: number | "";
  speed: number | "";
  beam_density: number | "";
  laser_rate: number | "";
  raw_material_rate: number | "";
  bending: number | "";
}

export interface LaserSettings {
  density: number | "";
  profit_percent: number | "";
}

export interface LaserPayload {
  id?: string;
  title: string;
  settings: LaserSettings;
  items: (LaserInputs & { verification: string })[];
}

export interface LaserResult {
  items: (LaserInputs & {
    filename: string;
    length: number;
    width: number;
    total_length: number;
    total_width: number;
    weight: number;
    labour: number;
    material_cost: number;
    profit: number;
    unit_cost: number;
  })[];
}

export interface LaserHistoryEntry {
  id: string;
  title: string;
  timestamp: string;
  settings: LaserSettings;
  items: (VerifiedDrawing & { inputs: LaserInputs })[];
}

export const getLaserCostingHistory = async (): Promise<
  LaserHistoryEntry[]
> => {
  const { data } = await Axios.get<LaserHistoryEntry[]>(
    "/costing/laser/history"
  );
  return data;
};

export const verifyLaserDxfs = async (
  files: File[],
  units: string,
  purpose?: "nesting"
) => {
  const body = new FormData();
  files.forEach((file) => body.append("files", file));
  body.append("unitless_units", units);
  if (purpose) body.append("purpose", purpose);
  const { data } = await Axios.post<{ items: VerifiedDrawing[] }>(
    "/costing/laser/verify",
    body,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
  );
  return data.items;
};

export const calculateLaserCosting = async (payload: LaserPayload) => {
  const { data } = await Axios.post<LaserResult>(
    "/costing/laser/calculate",
    payload
  );
  return data;
};

export const calculateSheetNesting = async (payload: {
  length: number;
  width: number;
  thickness: number;
  spacing?: number;
  edge_margin?: number;
  items: { verification: string; quantity: number }[];
}) => {
  const { data } = await Axios.post<NestResult>(
    "/costing/laser/nest",
    payload,
    { timeout: 120000 }
  );
  return data;
};

export const exportLaserCosting = async (payload: LaserPayload) => {
  const { data, headers } = await Axios.post("/costing/laser/export", payload, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${
    payload.title.replace(/[<>:"/\\|?*]/g, "_") || "Laser Cutting Costing"
  }.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return headers["x-laser-costing-id"] as string;
};

export const laserErrorMessage = async (error: unknown): Promise<string> => {
  if (!isAxiosError(error)) {
    return "Could not complete the request. Please try again.";
  }
  let body: unknown = error.response?.data;
  if (body instanceof Blob) {
    try {
      body = JSON.parse(await body.text());
    } catch {
      // Proxies often return HTML, including for blob export requests.
      // Use the HTTP status below instead of displaying proxy HTML.
      body = undefined;
    }
  }
  if (body && typeof body === "object" && "error" in body) {
    const message = body.error;
    if (typeof message === "string" && message.trim()) return message;
  }
  const status = error.response?.status;
  switch (status) {
    case 413:
      return "The server rejected the upload because it is too large (HTTP 413). Try fewer or smaller DXFs.";
    case 401:
      return "Your session has expired. Sign in again and retry.";
    case 403:
      return "The server denied this request (HTTP 403). Check your account permissions.";
    case 404:
      return "The server could not find the laser costing endpoint (HTTP 404).";
    case 502:
    case 503:
      return `The laser costing service is unavailable (HTTP ${status}). Please try again shortly.`;
    case 504:
      return "The server timed out while processing the request (HTTP 504). Try fewer DXFs at a time.";
    default:
      if (status) {
        return `The server could not complete the request (HTTP ${status}). Please try again.`;
      }
  }
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return "The request timed out. Try fewer DXFs at a time.";
  }
  return "Could not reach the server. Check your connection and try again.";
};
